import type { SvgIconComponent } from '@mui/icons-material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ElevatorIcon from '@mui/icons-material/Elevator';
import HandymanIcon from '@mui/icons-material/Handyman';
import InboxIcon from '@mui/icons-material/Inbox';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import type { SvgIconProps } from '@mui/material';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { captureException } from '@sentry/react';
import { useEffect } from 'react';
import {
  createBrowserRouter,
  isRouteErrorResponse,
  Outlet,
  redirect,
  useRouteError,
} from 'react-router';

import { usePageTracking } from '~/analytics';
import { PageHeader } from '~/components/global/PageHeader';
import { ContractIcon, InvoiceIcon, PortfolioIcon, TicketIcon } from '~/components/icons';
import { generateErrorBoundary } from '~/components/utils/errorBoundaries';
import { AppShell, loader as appShellLoader } from '~/routes/_app';
import {
  documentReviewLoader,
  toolsDocumentReviewLoader,
} from '~/routes/tools/document-review/guard';
import { liftAiAuth } from '~/utils/api';
import { checkFeatureFlag } from '~/utils/launchDarkly';
import { canAccess, getDefaultRouteForRole, NAVIGATE_TO_BACKLOG_PAGE } from '~/utils/role';

// Top-level route error boundary. Mounts inside the AppShell <div>, so
// it must NOT contain document-level elements (<html>/<head>/<body>) —
// those are illegal as children of <div> and trigger React's
// validateDOMNesting, masking the underlying route error. This is a
// SPA, so the document root is provided by index.html.
//
// Exported for unit testing — see __tests__/router.test.tsx.
export const ErrorBoundary = () => {
  const error = useRouteError();

  useEffect(() => {
    captureException(error);
  }, [error]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      p={2}
      height="100%"
      flex="1 0 auto"
      bgcolor="#F9FAFB"
    >
      <img width="200px" src="/images/lift-logo.svg" alt="Lift AI Logo" />
      <Typography variant="h2" pt={4} whiteSpace="pre-wrap" textAlign="center">
        {isRouteErrorResponse(error)
          ? `${error.status}\n${error.statusText}`
          : error instanceof Error
            ? error.message
            : 'Unknown Error'}
      </Typography>
      {isRouteErrorResponse(error) && error.status === 403 ? (
        <Button href="/logout">Logout and try again</Button>
      ) : null}
    </Box>
  );
};

const generateTopLevelErrorBoundary = ({
  title,
  icon,
}: {
  title: string;
  icon: SvgIconComponent | React.FC<SvgIconProps>;
}) => {
  const ErrorBoundary = generateErrorBoundary();

  return (
    <>
      <PageHeader title={title} icon={icon} />
      <ErrorBoundary />
    </>
  );
};

// Pathless root layout. Mounts `usePageTracking` so `page_viewed` fires
// uniformly for **every** route — both the authenticated tree (under
// `<AppShell />`) and the unauthenticated auth routes (`/login`,
// `/reset-password`, `/auth/*`, `/logout`). Without this, only the
// authenticated tree would track page views (the prior structure put
// `usePageTracking` inside `_app.tsx`), which silently broke any funnel
// that started with `page_viewed[/login]` or similar.
const RootLayout = () => {
  usePageTracking();
  return <Outlet />;
};

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/',
        element: <AppShell />,
        errorElement: <ErrorBoundary />,
        hydrateFallbackElement: <LinearProgress color="info" />,
        loader: appShellLoader,
        // Opts every authenticated route into the `usePageTracking` identity
        // gate: the first `page_viewed` here waits until `appShellLoader` has
        // identified the user, so cold loads aren't attributed to the
        // anonymous `$device` id. The auth routes below (`/login`, etc.) omit
        // this handle and stay anonymous.
        handle: { requiresIdentity: true },
        children: [
          {
            path: '',
            loader: async () => {
              const { requireUser } = liftAiAuth();
              const { userRole, userId, email } = await requireUser();
              // The Work Queue is the consultant landing page, but only while the
              // `work-queue` LaunchDarkly flag is on for the user. Off (or LD
              // unavailable) falls back to the role's default route; the
              // _app.inbox loader enforces the same gate for direct visits.
              if (canAccess(userRole, NAVIGATE_TO_BACKLOG_PAGE)) {
                const workQueueEnabled = await checkFeatureFlag(
                  'work-queue',
                  { key: userId, email: email ?? '', role: userRole },
                  false,
                );
                if (workQueueEnabled) {
                  throw redirect('/work-queue');
                }
              }
              throw redirect(getDefaultRouteForRole(userRole));
            },
          },
          {
            id: 'inbox',
            path: 'work-queue',
            lazy: () => import('~/routes/_app.inbox'),
            errorElement: generateTopLevelErrorBoundary({
              title: 'Needs Attention',
              icon: InboxIcon,
            }),
          },
          {
            id: 'tasks',
            path: 'tasks',
            errorElement: generateTopLevelErrorBoundary({
              title: 'Custom Tasks',
              icon: TaskAltIcon,
            }),
            children: [
              {
                path: '',
                lazy: () => import('~/routes/_app.tasks._index'),
              },
              {
                id: 'task-detail',
                path: ':taskId',
                lazy: () => import('~/routes/_app.tasks.$taskId'),
              },
            ],
          },
          {
            path: 'tickets',
            lazy: () => import('~/routes/_app.tickets._index'),
            errorElement: generateTopLevelErrorBoundary({
              title: 'Tickets',
              icon: TicketIcon,
            }),
          },
          {
            path: 'spending',
            errorElement: generateTopLevelErrorBoundary({
              title: 'Spendings',
              icon: InvoiceIcon,
            }),
            children: [
              {
                path: '',
                lazy: () => import('~/routes/_app.spending._index'),
              },
              {
                // Full-page invoice/proposal detail. Entry points still set
                // `?selectedInvoiceId=`; `DetailParamRedirects` converts that to a
                // navigation here.
                id: 'invoice-detail',
                path: ':invoiceId',
                lazy: () => import('~/routes/_app.spending.$invoiceId'),
              },
            ],
          },
          {
            path: 'portfolio',
            errorElement: generateTopLevelErrorBoundary({
              title: 'Portfolios',
              icon: PortfolioIcon,
            }),
            children: [
              {
                path: '',
                lazy: () => import('~/routes/_app.portfolio/_index'),
              },
              {
                // The property page's old home. Its loader forwards to /properties/:id, so
                // links already in the wild keep working.
                id: 'property-detail',
                path: ':id',
                lazy: () => import('~/routes/_app.portfolio/$id'),
              },
            ],
          },
          {
            // The property table, split out of /portfolio's Table tab.
            path: 'properties',
            errorElement: generateTopLevelErrorBoundary({
              title: 'Properties',
              icon: PortfolioIcon,
            }),
            children: [
              {
                path: '',
                lazy: () => import('~/routes/_app.properties/_index'),
              },
              {
                id: 'properties-property-detail',
                path: ':id',
                lazy: () => import('~/routes/_app.properties/$id'),
              },
              {
                id: 'properties-task-detail',
                path: ':id/tasks/:taskId',
                lazy: () => import('~/routes/_app.properties/$id.tasks.$taskId'),
              },
            ],
          },
          {
            // Named so the global filter bar can address it (see `useGlobalFilters`) — an
            // auto-generated id would move the moment a route above it changed.
            id: 'compliance',
            path: 'compliance',
            errorElement: generateTopLevelErrorBoundary({
              title: 'Elevators',
              icon: ElevatorIcon,
            }),
            children: [
              {
                path: '',
                lazy: () => import('~/routes/_app.compliance._index'),
              },
              {
                // Full-page elevator detail. Entry points still set `?selectedCarId=` (or
                // `?carId=&car_panel=open` on the list itself); those become a navigation
                // here.
                id: 'elevator-detail',
                path: ':carId',
                lazy: () => import('~/routes/_app.compliance.$carId'),
              },
            ],
          },
          {
            id: 'contracts',
            path: 'contracts',
            errorElement: generateTopLevelErrorBoundary({
              title: 'Contracts',
              icon: ContractIcon,
            }),
            children: [
              {
                path: '',
                lazy: () => import('~/routes/_app.contracts._index'),
              },
              {
                // Full-page contract detail. Entry points still set
                // `?selectedContractId=`; `DetailParamRedirects` converts that to a
                // navigation here.
                id: 'contract-detail',
                path: ':contractId',
                lazy: () => import('~/routes/_app.contracts.$contractId'),
              },
            ],
          },
          {
            // Do NOT add a `:param` child here. A generated report's link resolves
            // through the top-level `/reports/:publicId` route at the bottom of this
            // file; a nested `reports/:something` would score identically and win on
            // definition order, silently sending every report link back to the 404
            // it used to hit (AMP-733). Nest a literal segment instead, or extend the
            // top-level route.
            id: 'reports',
            path: 'reports',
            lazy: () => import('~/routes/_app.reports._index'),
            errorElement: generateTopLevelErrorBoundary({
              title: 'Reports',
              icon: AutoAwesomeIcon,
            }),
          },
          {
            // Legacy /insights path: its loader redirects to /reports so old
            // links and bookmarks keep working.
            path: 'insights',
            lazy: () => import('~/routes/_app.insights._index'),
          },
          {
            path: 'intelligence',
            loader: () => {
              throw redirect('/');
            },
          },
          {
            id: 'admin',
            path: 'admin',
            lazy: () => import('~/routes/_app.admin._index'),
            errorElement: generateTopLevelErrorBoundary({
              title: 'Admin',
              icon: AdminPanelSettingsIcon,
            }),
          },
          {
            id: 'ai-extracted',
            path: 'ai-extracted',
            loader: documentReviewLoader,
            errorElement: generateTopLevelErrorBoundary({
              title: 'AI Pipeline Uploads',
              icon: CloudUploadIcon,
            }),
            // Consultant-facing mirror of the document-review portal; reuses the
            // operator console's detail + split views so the whole flow stays
            // under /ai-extracted (never /tools) for consultants.
            children: [
              {
                path: '',
                lazy: () => import('~/routes/_app.ai-extracted._index'),
              },
              {
                path: ':uploadId',
                lazy: () => import('~/routes/tools/document-review/$uploadId'),
              },
              {
                path: ':uploadId/split',
                lazy: () => import('~/routes/tools/document-review/$uploadId.split'),
              },
            ],
          },
          {
            path: 'account/password',
            lazy: () => import('~/routes/account/password'),
          },
          {
            id: 'tools',
            path: 'tools',
            errorElement: generateTopLevelErrorBoundary({
              title: 'Tools',
              icon: HandymanIcon,
            }),
            children: [
              {
                path: '',
                lazy: () => import('~/routes/tools/index'),
              },
              {
                path: 'tickets-csv',
                lazy: () => import('~/routes/tools/tickets-csv'),
              },
              {
                path: 'impersonate',
                lazy: () => import('~/routes/tools/impersonate'),
              },
              {
                path: 'document-review',
                loader: toolsDocumentReviewLoader,
                children: [
                  {
                    path: '',
                    lazy: () => import('~/routes/tools/document-review/index'),
                  },
                  {
                    path: 'email/:emailId',
                    lazy: () => import('~/routes/tools/document-review/email/$emailId'),
                  },
                  {
                    path: ':uploadId',
                    lazy: () => import('~/routes/tools/document-review/$uploadId'),
                  },
                  {
                    path: ':uploadId/split',
                    lazy: () => import('~/routes/tools/document-review/$uploadId.split'),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '/login',
        lazy: () => import('~/routes/login'),
      },
      {
        path: '/logout',
        lazy: () => import('~/routes/logout'),
      },
      {
        path: '/reset-password',
        lazy: () => import('~/routes/reset-password'),
      },
      // The API builds emailed links to these two paths (`LINK_PATHS` in
      // services/api/native_auth/links.py). Change both together.
      {
        path: '/auth/magic-link',
        lazy: () => import('~/routes/magic-link'),
      },
      {
        path: '/auth/reset-password',
        lazy: () => import('~/routes/reset-password/confirm'),
      },
      // Accounts are provisioned by LiftAI, so there is no self-service signup.
      // Old links to it land on sign-in.
      {
        path: '/signup',
        loader: () => redirect('/login'),
      },
    ],
  },
  {
    // A generated report's customer-facing link is `<webapp origin>/reports/<id>/`
    // even though Django serves the file — see the route module for why. It sits
    // outside `<RootLayout />` on purpose: the link is shareable and usually opens
    // in the report viewer's iframe, so it must not require a session and must not
    // emit a `page_viewed` for every frame load.
    path: '/reports/:publicId',
    lazy: () => import('~/routes/reports.$publicId'),
    errorElement: <ErrorBoundary />,
    hydrateFallbackElement: <LinearProgress color="info" />,
  },
]);
